import { PriceObjectDto } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { convertImageFromDto, convertImageToDto } from "@modules/dtos/imageDto"
import { ExploreProgramBundleItemDto } from "@modules/explore/gateway/exploreDto"
import { convertCoachFromDto, convertUserFromDto } from "@modules/profile/profileDtos"
import { Coach, User } from "@modules/profile/profileTypes"
import {
  LightProgramBundleInfoDto,
  ProgramBundleDraftDto,
  ProgramBundleDto,
  ProgramBundleType,
} from "@modules/programBundle/api/programBundleDto"
import { ProgramVisibility, UserProgramItem } from "@modules/programs/programTypes"
import { convertUserProgramItemFromDto } from "@modules/programs/services/programDto"
import { ProgramBundleLightDto } from "@modules/programs/services/programInfiniteDto"
import { HUImage } from "@modules/types/imageTypes"

export class ProgramBundleDraftEntity {
  name: string
  description: string
  visibility: ProgramVisibility
  type: ProgramBundleType
  imageWeb: HUImage | null
  price: PriceObjectDto | null
  priceInput: number | null // Use only for the form
  programIds: string[]
  addFreeTrial: boolean
  trialDuration: number | null

  constructor(programBundle: ProgramBundleDraftEntity) {
    this.type = programBundle.type
    this.name = programBundle.name
    this.description = programBundle.description
    this.visibility = programBundle.visibility
    this.imageWeb = programBundle.imageWeb
    this.price = programBundle.price
    this.programIds = programBundle.programIds
    this.priceInput = programBundle.price ? programBundle.price.value : null
    this.addFreeTrial = !!programBundle.trialDuration
    this.trialDuration = programBundle.trialDuration
  }

  static createDraft(): ProgramBundleDraftEntity {
    return new ProgramBundleDraftEntity({
      type: ProgramBundleType.VIRTUAL,
      name: "",
      description: "",
      visibility: ProgramVisibility.PRIVATE,
      imageWeb: null,
      price: null,
      priceInput: null,
      programIds: [],
      addFreeTrial: false,
      trialDuration: null,
    })
  }

  static fromProgramBundleEntity(programBundle: ProgramBundleEntity): ProgramBundleDraftEntity {
    return new ProgramBundleDraftEntity({
      type: programBundle.type,
      name: programBundle.name,
      description: programBundle.description,
      visibility: programBundle.visibility,
      imageWeb: programBundle.image ? programBundle.image : null,
      price: programBundle.price,
      programIds: programBundle.programs?.map((program) => program.id) ?? [],
      priceInput: programBundle.price ? programBundle.price.value : null,
      addFreeTrial: !!programBundle.trialDuration,
      trialDuration: programBundle.trialDuration,
    })
  }

  static toDto(programBundle: ProgramBundleDraftEntity): ProgramBundleDraftDto {
    return {
      type: programBundle.type,
      name: programBundle.name,
      description: programBundle.description,
      visibility: programBundle.visibility,
      imageWeb: programBundle.imageWeb ? convertImageToDto(programBundle.imageWeb) : null,
      price: programBundle.price,
      programIds: programBundle.programIds,
      trialDuration: programBundle.trialDuration ?? null,
    }
  }
}

export class ProgramBundleEntity {
  id: string
  type: ProgramBundleType
  name: string
  description: string
  visibility: ProgramVisibility
  image: HUImage | null
  invitationLink?: string | null
  owner: Coach
  subscriptionCount: number
  price: PriceObjectDto | null
  programs?: UserProgramItem[]
  nbPrograms: number
  trialDuration: number | null
  isEligibleToTrial: boolean | null

  private constructor(programBundle: ProgramBundleEntity) {
    this.id = programBundle.id
    this.type = programBundle.type
    this.name = programBundle.name
    this.description = programBundle.description
    this.visibility = programBundle.visibility
    this.image = programBundle.image
    this.invitationLink = programBundle.invitationLink
    this.owner = programBundle.owner
    this.subscriptionCount = programBundle.subscriptionCount
    this.price = programBundle.price
    this.programs = programBundle.programs
    this.nbPrograms = programBundle.nbPrograms
    this.trialDuration = programBundle.trialDuration
    this.isEligibleToTrial = programBundle.isEligibleToTrial
  }

  static fromDto(dto: ProgramBundleDto): ProgramBundleEntity {
    return new ProgramBundleEntity({
      ...dto,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: convertCoachFromDto(dto.owner),
      programs: dto.programs.map((program) => convertUserProgramItemFromDto(program)),
      type: dto.type,
    })
  }
}

export class ProgramBundleItemEntity {
  id: string
  type: ProgramBundleType
  name: string
  visibility: ProgramVisibility
  image: HUImage | null
  price: PriceObjectDto | null
  subscriptionCount: number
  nbPrograms: number
  owner: User

  private constructor(programBundle: ProgramBundleItemEntity) {
    this.id = programBundle.id
    this.type = programBundle.type
    this.name = programBundle.name
    this.image = programBundle.image
    this.price = programBundle.price
    this.subscriptionCount = programBundle.subscriptionCount
    this.nbPrograms = programBundle.nbPrograms
    this.visibility = programBundle.visibility
    this.owner = programBundle.owner
  }

  static fromDto(dto: ExploreProgramBundleItemDto): ProgramBundleItemEntity {
    return new ProgramBundleItemEntity({
      ...dto,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: convertUserFromDto(dto.owner),
    })
  }
}

export class ProgramBundleLightEntity {
  programBundleId: string
  programBundleName: string
  paidPrice: PriceObjectDto | null
  type: ProgramBundleType

  constructor(programBundle: ProgramBundleLightEntity) {
    this.programBundleId = programBundle.programBundleId
    this.programBundleName = programBundle.programBundleName
    this.paidPrice = programBundle.paidPrice
    this.type = programBundle.type
  }

  static fromDto(dto: ProgramBundleLightDto, type: ProgramBundleType): ProgramBundleLightEntity {
    return new ProgramBundleLightEntity({
      programBundleId: dto.programBundleId,
      programBundleName: dto.programBundleName,
      paidPrice: dto.paidPrice,
      type,
    })
  }
}

export class LightProgramBundleInfo {
  id: string
  name: string
  image: HUImage | null
  price: PriceObjectDto | null
  type: ProgramBundleType

  private constructor(programBundle: LightProgramBundleInfo) {
    this.id = programBundle.id
    this.name = programBundle.name
    this.image = programBundle.image
    this.price = programBundle.price
    this.type = programBundle.type
  }

  static fromDto(dto: LightProgramBundleInfoDto): LightProgramBundleInfo {
    return new LightProgramBundleInfo({
      ...dto,
      image: dto.image ? convertImageFromDto(dto.image) : null,
    })
  }
}
